import * as $3Dmol from '3dmol';
import React, { useEffect, useRef } from 'react';
import { verboseLog, verboseError } from 'src/utils/config';

// Define the prop types for the PDBViewer component
interface PDBViewerProps {
    pdbUrl: string | null; // URL of the PDB file to be loaded (can be null)
}

const PDBViewer: React.FC<PDBViewerProps> = ({ pdbUrl }) => {
    const viewerRef = useRef<HTMLDivElement | null>(null); // Reference to the div that will hold the 3Dmol viewer

    useEffect(() => {
        const element = viewerRef.current; // Access the div element
        if (!element) return; // Ensure the element is available

        // Don't render if no PDB URL is provided
        if (!pdbUrl) {
            verboseLog('No PDB URL provided, skipping 3D viewer');
            return;
        }

        const config = { backgroundColor: 'white' }; // Viewer configuration
        const viewer = $3Dmol.createViewer(element, config); // Create the 3Dmol viewer

        // Fetch the PDB file
        fetch(pdbUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch PDB file: ${response.status} ${response.statusText}`);
                }
                return response.text();
            })
            .then((data) => {
                viewer.addModel(data, 'pdb'); // Add the PDB model
                viewer.setStyle({}, { cartoon: { color: 'spectrum' } }); // Set the style (cartoon)
                viewer.zoomTo(); // Zoom to fit the model
                viewer.render(); // Render the model
                verboseLog('Successfully loaded PDB file');
            })
            .catch((error) => {
                verboseError('Error loading PDB file:', error);
                // Optionally show a user-friendly error message in the viewer
                element.innerHTML = '<div style="text-align: center; padding: 20px; color: #666;">3D model not available</div>';
            });

        // Handle resize events
        const handleResize = () => {
            viewer.resize(); // Resize the viewer when the window is resized
        };

        window.addEventListener('resize', handleResize);

        // Cleanup event listeners and viewer on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [pdbUrl]);

    return (
        <div
            ref={viewerRef}
            style={{
                width: '100%', // Full width of container
                height: '100%', // Full height of container
                minHeight: '180px', // Minimum height for visibility
                position: 'relative', // Ensure it's relatively positioned
                borderRadius: '8px', // Rounded corners to match design
                overflow: 'hidden' // Hide overflow
            }}
        />
    );
};

export default PDBViewer;
